import { Box, Text } from '@chakra-ui/react'
import { useState, useEffect } from 'react'

type TeamColor = 'red' | 'blue' | 'purple' | 'yellow' | 'orange' | 'green'

interface ScoreBoxProps {
  color: TeamColor
  score: number
  name?: string
  isController: boolean
  onClick: () => void
  isDarkTheme?: boolean
  team1Name?: string
  team2Name?: string
  isTeam1?: boolean
  lastScoringTeam?: 'team1' | 'team2' | 'none' | null
}

export function ScoreBox({
  color,
  score,
  name,
  isController,
  onClick,
  isDarkTheme = false,
  team1Name,
  team2Name,
  isTeam1,
  lastScoringTeam
}: ScoreBoxProps) {
  const [isAnimating, setIsAnimating] = useState(false)

  // Trigger animation when this team scores (but not on initial render)
  useEffect(() => {
    if (!isDarkTheme && lastScoringTeam && 
        ((isTeam1 && lastScoringTeam === 'team1') || (!isTeam1 && lastScoringTeam === 'team2'))) {
      setIsAnimating(true)
      const timer = setTimeout(() => {
        setIsAnimating(false)
      }, 2500)
      return () => clearTimeout(timer)
    }
  }, [lastScoringTeam, isDarkTheme, isTeam1, score])

  const getColor = (teamColor: TeamColor) => {
    return teamColor === 'blue' ? '#2e4fcb' :
           teamColor === 'red' ? '#dd0000' :
           teamColor === 'green' ? '#38a169' :
           teamColor === 'orange' ? '#f73e01' :
           teamColor === 'purple' ? '#8f00c7' :
           teamColor === 'yellow' ? '#e2e700' : 'gray'
  }

  const phonetic = color === 'red' ? 'Romeo' :
                   color === 'blue' ? 'Bravo' :
                   color === 'green' ? 'Golf' :
                   color === 'yellow' ? 'Yankee' :
                   color === 'orange' ? 'Oscar' : 'Charlie'

  const textColor = (color === 'yellow' || color === 'orange') ? 'black' : 'white'

  // Determine display name
  const getDisplayName = () => {
    if (name && name.trim() !== '') {
      return name
    }
    
    // If this team has no name, check if the other team has a name
    const otherTeamName = isTeam1 ? team2Name : team1Name
    if (otherTeamName && otherTeamName.trim() !== '') {
      return 'Opponent'
    }
    
    return null // Show nothing if both teams have no names
  }

  const displayName = getDisplayName()

  return (
    <Box
      w="45%"
      p={4}
      bg={isDarkTheme ? '#101010' : (isAnimating ? 'black' : getColor(color))}
      borderRadius="lg"
      textAlign="center"
      flexShrink={0}
      onClick={isController ? onClick : undefined}
      cursor={isController ? 'pointer' : 'default'}
      border={`2px solid ${getColor(color)}`}
    >
      {displayName && (
        <Text fontSize="xl" fontWeight="bold" mb={2} color={isDarkTheme ? '#747474' : (isAnimating ? getColor(color) : textColor)}>
          {displayName}
        </Text>
      )}
      <Text
        fontSize={["80px", "100px", "120px", "140px", "160px"]}
        fontWeight="bold"
        color={isDarkTheme ? getColor(color) : (isAnimating ? (color === 'yellow' ? getColor(color) : 'white') : textColor)}
        animation={isAnimating ? "scaleEffect 2.5s ease-out" : "none"}
        sx={{
          '@keyframes scaleEffect': {
            '0%': { transform: 'scale(1.6)', opacity: 0 },
            '50%': { transform: 'scale(1.6)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 1 }
          }
        }}
      >
        {score}
      </Text>
      <Box mt={2}>
        {isController ? (
          <Box
            display="flex"
            flexDirection={{ base: "column", sm: "row" }}
            alignItems="center"
            justifyContent="center"
            gap={{ base: 0, sm: 1 }}
            flexWrap="wrap"
          >
            <Text fontSize="sm" color={isDarkTheme ? '#747474' : (isAnimating ? getColor(color) : textColor)}>
              Say "{phonetic}" or "{color.charAt(0).toUpperCase() + color.slice(1)}"
            </Text>
          </Box>
        ) : (
          <Text fontSize="xs" color={isDarkTheme ? '#747474' : (isAnimating ? getColor(color) : textColor)}>
            Display Only
          </Text>
        )}
      </Box>
    </Box>
  )
} 